/**
 * Contrato de la autodetección GPS por encuesta.
 *
 * §9.4 — "GPS obligatorio: validar que cada captura cae dentro del polígono de
 * la sección INE."
 *
 * Este módulo define los tipos que comparten la PWA y el API. La detección
 * autoritativa ocurre en el servidor (PostGIS), pero la PWA repite la misma
 * evaluación en local contra las geometrías cacheadas para poder trabajar sin
 * señal y avisar al encuestador en el momento, no horas después.
 */
/* -------------------------------------------------------------------------- */
/*            Motivos de captura fuera del polígono esperado                  */
/* -------------------------------------------------------------------------- */
/**
 * El coordinador eligió que la PWA ADVIERTA Y PERMITA continuar con motivo
 * obligatorio, en lugar de bloquear. La razón es que un bloqueo duro tiraría
 * trabajo de campo legítimo por errores de cartografía —el caso de la sección
 * 722 reseccionada es exactamente eso— o por GPS impreciso en interiores.
 *
 * Toda captura fuera de polígono queda marcada para revisión del supervisor
 * con su motivo y su distancia en metros, de modo que el control de §9.4 sigue
 * siendo auditable.
 */
export const OUT_OF_POLYGON_REASONS = [
    {
        code: 'cartografia_desactualizada',
        label: 'La cartografía no coincide con el territorio',
        description: 'La manzana o colonia existe pero el polígono cargado no la cubre. Caso típico de sección reseccionada.',
    },
    {
        code: 'vivienda_en_limite',
        label: 'Vivienda en el límite de la sección',
        description: 'La vivienda está sobre la línea divisoria entre dos secciones.',
    },
    {
        code: 'gps_impreciso',
        label: 'Señal GPS imprecisa',
        description: 'Lectura tomada en interior, entre construcciones altas o con mala recepción.',
    },
    {
        code: 'reasignacion_supervisor',
        label: 'Reasignación autorizada por el supervisor',
        description: 'El supervisor autorizó levantar en un área distinta a la asignada.',
    },
    {
        code: 'error_de_asignacion',
        label: 'Error en la asignación recibida',
        description: 'El conglomerado asignado no corresponde al territorio donde se está trabajando.',
    },
    {
        code: 'otro',
        label: 'Otro motivo',
        description: 'Requiere descripción escrita.',
    },
];
/**
 * Jerarquía de validación: la autodetección usa la capa más fina que esté
 * cargada. Si no hay manzanas INEGI, valida contra el conglomerado; si no hay
 * conglomerados trazados, contra la sección.
 */
export const GEOMETRY_PRECISION_ORDER = [
    'manzana',
    'conglomerado',
    'area-extraordinaria',
    'seccion',
];
/* -------------------------------------------------------------------------- */
/*                       Utilidades geométricas locales                       */
/* -------------------------------------------------------------------------- */
/**
 * Distancia en metros entre dos coordenadas (haversine).
 *
 * Se usa en el cliente para la evaluación offline; en el servidor la distancia
 * autoritativa la calcula PostGIS con `ST_Distance` sobre geografía.
 */
export function haversineMeters(a, b) {
    const R = 6_371_008.8;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.latitude - a.latitude);
    const dLon = toRad(b.longitude - a.longitude);
    const lat1 = toRad(a.latitude);
    const lat2 = toRad(b.latitude);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
/**
 * Punto en polígono por ray casting, con soporte de huecos.
 *
 * El KMZ del INE usa `innerBoundaryIs` para huecos, así que ignorarlos
 * clasificaría como "dentro" un punto que está en un enclave de otra sección.
 */
export function pointInPolygon(point, polygon) {
    const [outer, ...holes] = polygon;
    if (!outer || !pointInRing(point, outer))
        return false;
    return !holes.some((hole) => pointInRing(point, hole));
}
function pointInRing(point, ring) {
    const x = point.longitude;
    const y = point.latitude;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const pi = ring[i];
        const pj = ring[j];
        const xi = pi[0];
        const yi = pi[1];
        const xj = pj[0];
        const yj = pj[1];
        const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersects)
            inside = !inside;
    }
    return inside;
}
/** Punto dentro de un multipolígono. */
export function pointInMultiPolygon(point, multi) {
    return multi.some((p) => pointInPolygon(point, p));
}
/**
 * Distancia aproximada en metros de un punto al borde de un polígono.
 * Devuelve 0 si el punto está dentro.
 *
 * Proyecta a un plano local equirectangular alrededor del punto, lo que a la
 * escala de una manzana o una sección introduce un error despreciable.
 */
export function distanceToPolygonMeters(point, polygon) {
    if (pointInPolygon(point, polygon))
        return 0;
    const latRad = (point.latitude * Math.PI) / 180;
    const mPerDegLat = 111_132.92 - 559.82 * Math.cos(2 * latRad) + 1.175 * Math.cos(4 * latRad);
    const mPerDegLon = 111_412.84 * Math.cos(latRad) - 93.5 * Math.cos(3 * latRad);
    const px = point.longitude * mPerDegLon;
    const py = point.latitude * mPerDegLat;
    let min = Infinity;
    for (const ring of polygon) {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const a = ring[j];
            const b = ring[i];
            const d = pointSegmentDistance(px, py, a[0] * mPerDegLon, a[1] * mPerDegLat, b[0] * mPerDegLon, b[1] * mPerDegLat);
            if (d < min)
                min = d;
        }
    }
    return min === Infinity ? Infinity : min;
}
function pointSegmentDistance(px, py, ax, ay, bx, by) {
    const dx = bx - ax;
    const dy = by - ay;
    const lengthSq = dx * dx + dy * dy;
    if (lengthSq === 0)
        return Math.hypot(px - ax, py - ay);
    let t = ((px - ax) * dx + (py - ay) * dy) / lengthSq;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
/** Caja envolvente de un polígono: [minLon, minLat, maxLon, maxLat]. */
export function boundingBox(polygon) {
    let minLon = Infinity;
    let minLat = Infinity;
    let maxLon = -Infinity;
    let maxLat = -Infinity;
    for (const ring of polygon) {
        for (const [lon, lat] of ring) {
            if (lon < minLon)
                minLon = lon;
            if (lat < minLat)
                minLat = lat;
            if (lon > maxLon)
                maxLon = lon;
            if (lat > maxLat)
                maxLat = lat;
        }
    }
    return [minLon, minLat, maxLon, maxLat];
}
export function inBoundingBox(point, [minLon, minLat, maxLon, maxLat]) {
    return (point.longitude >= minLon &&
        point.longitude <= maxLon &&
        point.latitude >= minLat &&
        point.latitude <= maxLat);
}
/* -------------------------------------------------------------------------- */
/*                  Captura fuera del área: quién puede                       */
/* -------------------------------------------------------------------------- */
/**
 * Estados que impiden capturar a un ENCUESTADOR.
 *
 * Cambio de política respecto de la decisión original de §5 de DECISIONES
 * («advertir, no bloquear»), por tres razones que el campo hizo visibles:
 *
 *  1. Los tres motivos que justificaban advertir ya no aplican igual. La
 *     cartografía desactualizada era el caso de la 722, y hoy las dos
 *     secciones reseccionadas se resuelven por equivalencia; la vivienda
 *     sobre la línea la cubre el buffer de 30 m, que sigue contando como
 *     válida; y el GPS impreciso cae en `low-accuracy`, que pide reintentar.
 *  2. De las 26 capturas con GPS de las primeras semanas, 7 cayeron en otra
 *     sección. Ninguna declaró cartografía, borde ni imprecisión: los motivos
 *     fueron «error de asignación» y «reasignación del supervisor». Para los
 *     dos la respuesta correcta es corregir la asignación en el panel, no
 *     levantar la entrevista donde no toca.
 *  3. Dejar fuera `unavailable` abriría la puerta de atrás: bastaría apagar
 *     la ubicación para capturar en cualquier sitio. Ninguna entrevista
 *     completada carece hoy de coordenada, así que cerrarla no cuesta nada.
 *
 * La excepción es por ROL y no absoluta, pero conviene saber a quién alcanza
 * de verdad: hoy sólo `superadmin` y `encuestador` tienen permiso de captura,
 * así que en el campo esto significa que NADIE puede levantar fuera de su
 * área. El supervisor no la necesita porque no captura; lo que hace es
 * corregir la asignación desde el panel, que es la salida correcta cuando el
 * motivo real era «error de asignación» —el más frecuente de los declarados—.
 */
export const BLOCKED_FOR_ENCUESTADOR = [
    'inside-other-section',
    'outside-all-sections',
    'unavailable',
    /**
     * `low-accuracy` sólo se devuelve cuando el punto NO cayó donde debía Y la
     * lectura es imprecisa: una lectura mala dentro del polígono esperado sigue
     * siendo válida. Es decir, aquí ya hay discrepancia y lo único que falta es
     * certeza. Dejarlo pasar sería el mismo hueco que `unavailable` con un paso
     * más: basta una lectura mala para capturar donde sea.
     *
     * No es un callejón sin salida: a diferencia de los otros tres, se resuelve
     * saliendo al aire libre y reintentando, y el mensaje lo dice así.
     */
    'low-accuracy',
];
/** ¿Este rol puede capturar con esta detección? */
export function canCaptureWith(status, role) {
    if (role !== 'encuestador')
        return true;
    return !BLOCKED_FOR_ENCUESTADOR.includes(status);
}
/**
 * Qué debe hacer el encuestador cuando no puede capturar.
 *
 * Importa distinguirlo del motivo del bloqueo: «vuelve a intentarlo» y «avisa
 * a tu supervisor» son acciones distintas, y decirle la equivocada lo deja
 * parado en la puerta de alguien.
 */
export function blockedRemedy(status) {
    switch (status) {
        case 'low-accuracy':
            return 'Sal al aire libre, aléjate de paredes y techos, y vuelve a tomar la lectura.';
        case 'unavailable':
            return 'Activa la ubicación del teléfono y vuelve a intentarlo al aire libre.';
        case 'inside-other-section':
        case 'outside-all-sections':
            return 'Avisa a tu supervisor: si la asignación está mal, él la corrige desde el panel y la entrevista se puede levantar.';
        default:
            return 'Avisa a tu supervisor.';
    }
}
//# sourceMappingURL=types.js.map